import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Form from "../components/Form.js";
import Input from "../components/input.js";
import Button from "../components/Button.js";
import Footer from "../components/Footer.js";
import Nav from "../components/Navbar.js";
import { getApiBaseUrl } from "../utils/apiConfig";
import "./AdminLogin.css";

export default function AdminLogin() {
  document.title = "Soloway | Admin Login";

  const nav = useNavigate();
  const [emailOrUsername, setEU] = useState("");
  const [password, setPw] = useState("");
  const [showChange, setShowChange] = useState(false);
  const [cpIdentifier, setCpIdentifier] = useState("");
  const [cpOld, setCpOld] = useState("");
  const [cpNew, setCpNew] = useState("");
  const [cpConfirm, setCpConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [cpBusy, setCpBusy] = useState(false);

  const API = getApiBaseUrl();

  async function onLogin() {
    if (!emailOrUsername || !password) {
      Swal.fire({
        title: "Missing fields",
        text: "Please enter your username and password.",
        icon: "warning",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: 'custom-swal-confirm'
        },
        buttonsStyling: false,
      });
      return;
    }
    try {
      setBusy(true);
      // admins table only has username; we still call it "identifier" for UI consistency
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: emailOrUsername, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Login failed");

      // store token
      localStorage.setItem("adminToken", data.token);

      await Swal.fire({
        title: "Welcome",
        text: "Login successful!",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });

      nav("/admin/dashboard");
    } catch (e) {
      Swal.fire({
        title: "Error",
        text: e.message,
        icon: "error",
        confirmButtonText: "Try Again",
        customClass: {
          confirmButton: 'custom-swal-cancel'
        },
        buttonsStyling: false,
      });
    } finally {
      setBusy(false);
    }
  }

  async function onChangePassword() {
    if (!cpIdentifier || !cpOld || !cpNew || !cpConfirm) {
      Swal.fire({
        title: "Missing fields",
        text: "Please fill all password fields.",
        icon: "warning",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: 'custom-swal-confirm'
        },
        buttonsStyling: false,
      });
      return;
    }
    if (cpNew !== cpConfirm) {
      Swal.fire({
        title: "Mismatch",
        text: "New password and confirmation do not match.",
        icon: "warning",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: 'custom-swal-confirm'
        },
        buttonsStyling: false,
      });
      return;
    }
    try {
      setCpBusy(true);
      const res = await fetch(`${API}/admin/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: cpIdentifier,
          currentPassword: cpOld,
          newPassword: cpNew,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to change password");

      await Swal.fire({
        title: "Success",
        text: "Password updated successfully.",
        icon: "success",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: 'custom-swal-confirm'
        },
        buttonsStyling: false,
      });
      setShowChange(false);
      setCpIdentifier("");
      setCpOld("");
      setCpNew("");
      setCpConfirm("");
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    } finally {
      setCpBusy(false);
    }
  }

  return (
    <>
    <Nav/>
    <div className="login-container">
      <main className="login-main">
        <Form
          title="Admin Login"
          subtitle="Sign in to manage travelers, guides, hotels and more."
        >
          <Input
            label="Admin Username"
            value={emailOrUsername}
            onChange={(e) => setEU(e.target.value)}
            placeholder="Enter Username (admin)"
          />
          <Input
            label="Admin Password"
            type="password"
            value={password}
            onChange={(e) => setPw(e.target.value)}
            placeholder="••••••••"
          />

          <div className="change-password-link">
            <span>Need to update your password?</span>
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                setShowChange(true);
                setCpIdentifier(emailOrUsername || "");
              }}
            >
              Change Password
            </button>
          </div>

          <Button type="submit" className="btn btn-primary" onClick={onLogin} disabled={busy}>
            {busy ? "Signing In..." : "Sign In"}
          </Button>

          <div className="register-link">
            <span>New admin?</span>
            <button type="button" className="link-btn" onClick={() => nav("/admin/register")}>
              Sign Up
            </button>
          </div>
        </Form>
      </main>

      {showChange && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-title">Change Admin Password</div>
            <form onSubmit={(e) => e.preventDefault()}>
              <Input
                label="Admin Username"
                value={cpIdentifier}
                onChange={(e) => setCpIdentifier(e.target.value)}
                placeholder="admin or admin@gmail.com"
              />
              <Input
                label="Current Password"
                type="password"
                value={cpOld}
                onChange={(e) => setCpOld(e.target.value)}
                placeholder="••••••••"
              />
              <div className="input-grid">
                <Input
                  label="New Password"
                  type="password"
                  value={cpNew}
                  onChange={(e) => setCpNew(e.target.value)}
                  placeholder="••••••••"
                />
                <Input
                  label="Confirm New Password"
                  type="password"
                  value={cpConfirm}
                  onChange={(e) => setCpConfirm(e.target.value)}
                  placeholder="••••••••"
                />
              </div>

              <Button type="button" className="btn btn-primary" onClick={onChangePassword} disabled={cpBusy}>
                {cpBusy ? "Updating..." : "Change Password"}
              </Button>

              <Button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  setShowChange(false);
                  setCpIdentifier("");
                  setCpOld("");
                  setCpNew("");
                  setCpConfirm("");
                }}
              >
                Close
              </Button>
            </form>
          </div>
        </div>
      )}
      <Footer/>
    </div>
    </>
  );
}

